// src/components/ProductModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Product, Customer, displayPrice, useHandleAddToCart, Modifier, formatProductNameForUrl } from '../utils/types'; // Ensure Modifier is imported
import { toast } from 'react-toastify';
import CustomerProfileModal from './CustomerProfileModal';
import { FiX } from 'react-icons/fi'; // Import close icon
import ModifierModal from './ModifierModal'; // Import ModifierModal if used within ProductModal
import ProductDetails from './ProductDetails'; // Import the new component

// Define a type for the price data expected from the parent
interface MTPriceData {
    price: number;
    role: string; // The role for which this price applies
}

// Define props for ProductModal, including the new ones
interface ProductModalProps {
    product: Product;
    onClose: () => void;
    // Add props passed down from ProductCard or parent
    customerForAction: Customer | null;
    isLoadingCustomerForAction: boolean;
    mtPriceData: MTPriceData | null | undefined;
    effectiveUserId: number | undefined;
    effectiveUserRole: string | undefined;
    effectiveUserIsAuthenticated: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
    product,
    onClose,
    // Destructure new props
    customerForAction,
    isLoadingCustomerForAction,
    mtPriceData,
    effectiveUserId,
    effectiveUserRole,
    effectiveUserIsAuthenticated
}) => {
    // Use the hook for cart actions AND modifier handling if needed within this modal
    const {
        handleAddToCart,
        showModifierModal,
        handleCloseModifierModal,
        modifiers,
        selectedProduct,
        handleModifierSelection,
        handleConfirmModifiers,
        selectedModifiers
    } = useHandleAddToCart();

    const imageRef = useRef<HTMLImageElement>(null); // Ref for potential animation

    // --- State for Customer Profile Modal ---
    const [showCustomerProfileModal, setShowCustomerProfileModal] = useState(false);

    // --- Add to Cart Handler specific to this modal ---
    const handleModalAddToCartClick = () => {
        // 1. Check Authentication (using passed prop)
        if (!effectiveUserIsAuthenticated) {
            toast.error("Please log in to add items.");
            return;
        }

        // 2. Check loading state (using passed prop)
        if (isLoadingCustomerForAction) {
            toast.info("Checking customer details...");
            return;
        }

        // 3. Check customer data and profile completeness (using passed prop)
        if (!customerForAction) {
            toast.error("Could not verify customer details. Please try again or contact support.");
            console.error("Modal Add to cart check failed: customerForAction is null");
            return;
        }
        const hasPhone = customerForAction.phone && String(customerForAction.phone).trim() !== '';
        const hasEmail = customerForAction.email && String(customerForAction.email).trim() !== '';

        if (!hasPhone || !hasEmail) {
            // 4. Show profile modal
            toast.warn("Please complete your profile (Phone and Email) before adding items.");
            setShowCustomerProfileModal(true);
        } else {
            // 5. Proceed with adding to cart (using the handleAddToCart from the hook)
            // Use the price/user details passed down
            const priceToAdd = mtPriceData?.price ?? product.product_value;
            // Pass the imageRef if you intend to use it for animation from the modal
            handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole);
            // Optionally close the ProductModal after adding
            // onClose();
        }
    };

    // --- Handler to close the Customer Profile Modal ---
    const handleCustomerProfileModalClose = () => {
        setShowCustomerProfileModal(false);
    };

    const baseUrl = process.env.PUBLIC_URL || '';
    const productUrlLink = product
        ? `${baseUrl}/product/${product.id}/${formatProductNameForUrl(product.product_name)}`
        : '';

    // --- Modal Render Logic ---
    return (
        <> {/* Use Fragment because CustomerProfileModal provides its own portal/container */}
            <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-40 p-4"> {/* ProductModal Overlay */}
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-6"> {/* ProductModal Container */}

                    <div className="flex justify-end">
                    {/* Close Button for ProductModal */}
                    <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl z-10">
                        <FiX size={24} />
                    </button>
                    </div>

                    {/* Product Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Image Column */}
                        <div ref={imageRef}> {/* Add ref if needed for animation */}
                            <img
                                src={product.media.length > 0 ? product.media[0].original_url : 'placeholder.jpg'}
                                alt={product.product_name}
                                className="w-full h-auto object-contain rounded"
                            />




                        </div>

                        {/* Details Column */}
                        <div>
                            <h2 className="text-2xl font-bold mb-2">
                                <a href={`${productUrlLink}`} target='_blank' rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    {product.product_name}
                                </a>
                            </h2>
                            <p className="text-sm text-gray-500 mb-4">SKU: {product.product_code}</p>

                            {/* Price Display */}
                            <div className="price-div  mb-4">
                                {effectiveUserIsAuthenticated && mtPriceData ? (
                                    <p className="text-lg text-green-600 font-semibold">
                                        Your VIP Price: <span className='font-bold'>{displayPrice(mtPriceData.price)}</span>
                                    </p>
                                ) : null}
                                <p className="text-md text-gray-700">
                                    SRP: <span className='font-semibold'>{displayPrice(product.product_value)}</span>
                                </p>
                            </div>

                            {/* Description */}
                            <p className="text-gray-700 mb-4">{product.product_desc || 'No description available.'}</p>

                            <div className='my-4'>
                                {/* Warranty (if available) */}
                                {product.warranty && (
                                    <p className="text-sm md:text-md text-gray-600 mb-4">Warranty: {product.warranty}</p>
                                )}

                                {/* Stock Status */}
                                <p className={`text-sm md:text-md font-medium mb-4 ${product.stock && product.stock > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                                    {product.stock && product.stock > 0 ? `In Stock` : 'Back Order'}
                                </p>
                                {/* (${product.stock} available) */}
                                {/* Add to Cart Button */}
                                {effectiveUserIsAuthenticated && (
                                    <button
                                        onClick={handleModalAddToCartClick}
                                        disabled={isLoadingCustomerForAction}
                                        className={`w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-150 ${isLoadingCustomerForAction ? 'opacity-50 cursor-wait' : ''}`}
                                    >
                                        {isLoadingCustomerForAction ? 'Checking...' : 'Add to Cart'}
                                    </button>
                                )}
                                {!effectiveUserIsAuthenticated && (
                                    <p className="text-sm text-red-500 mt-2">Please log in to add items.</p>
                                )}
                            </div>

                            <ProductDetails product={product} /> {/* Use your new component here */}

                        </div>
                    </div>
                </div>
            </div>

            {/* --- Render Customer Profile Modal Conditionally --- */}
            {/* This modal will render via its own portal structure */}
            {showCustomerProfileModal && customerForAction && (
                <CustomerProfileModal
                    initialCustomerData={customerForAction}
                    onClose={handleCustomerProfileModalClose}
                />
            )}

            {/* --- Render Modifier Modal Conditionally (if triggered by handleAddToCart) --- */}
            {/* This modal also likely renders via its own portal/structure */}
            {showModifierModal && selectedProduct && (
                <ModifierModal
                    product={selectedProduct}
                    modifiers={modifiers}
                    onClose={handleCloseModifierModal}
                    onModifierSelection={handleModifierSelection}
                    onConfirm={handleConfirmModifiers}
                    selectedModifiers={selectedModifiers}
                />
            )}
        </>
    );
};

export default ProductModal;
